<template>
  <div class="home">
    <header class="header">
      <nav class="nav">
        <div class="logo">
          <img src="@/assets/logo.png" alt="Tripseek" />
          <span>TripSeek</span>
        </div>
        <div class="nav-right">
          <div class="language-selector" @click="toggleLanguageDropdown">
            <span>
              {{ currentLanguageName }}
            </span>
            <i class="fas fa-chevron-down"></i>
            <div v-if="showLanguageDropdown" class="language-dropdown">
              <div
                v-for="(name, code) in orderedLanguageMap"
                :key="code"
                @click.stop="changeLanguage(code)"
                class="language-option"
                :class="{ active: $i18n.locale === code }"
              >
                {{ name }}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <main>
      <section class="hero">
        <div class="hero-content">
          <h1 class="hero-title">
            <span class="slogan">{{ $t("hero.title") }}</span>
            <span class="highlight">{{ $t("hero.keynote") }}</span>
          </h1>
          <p class="hero-description">
            {{ $t("hero.description") }}
          </p>
          <div class="new-feature">
            <span class="badge">{{ $t("hero.newFeature") }}</span>
            <span class="feature-text">{{ $t("hero.newFeatureText") }}</span>
          </div>
        </div>
        <div class="hero-image">
          <ChatBox />
        </div>
      </section>

      <section class="trips">
        <h2>{{ $t("trips.title") }}</h2>
        <div class="trips-grid">
          <div class="trip-card" v-for="(trip, index) in trips" :key="index"  @click="gochatbox(trip.value)">
            <div class="trip-content">
              <h3>{{ trip.name }}</h3>
              <div class="ai-badge">
                <i class="fas fa-magic"></i>
                <span>{{ $t("aiBadge") }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="ai-assistant">
        <div class="assistant-content">
          <div class="text-content">
            <h2>{{ $t("assistant.title") }}</h2>
            <p>
              {{ $t("assistant.description") }}
            </p>
            <ul class="features-list">
              <li>
                <i class="fas fa-check"></i> {{ $t("assistant.features.1") }}
              </li>
              <li>
                <i class="fas fa-check"></i> {{ $t("assistant.features.2") }}
              </li>
              <li>
                <i class="fas fa-check"></i> {{ $t("assistant.features.3") }}
              </li>
            </ul>
          </div>
          <div class="assistant-image">
            <img src="@/assets/logo.svg" alt="AI Assistant" />
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <p>&copy; {{ currentYear }} {{ $t("footer.copyright") }}.</p>
    </footer>
  </div>
</template>

<script>
import ChatBox from "./ChatBox.vue";

export default {
  name: "Home",
  components: {
    ChatBox,
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      showLanguageDropdown: false,
      languageMap: {
        zh: "中文",
        en: "English",
      },
    };
  },
  computed: {
    currentLanguage() {
      const browserLang = (
        navigator.language || navigator.userLanguage
      ).toLowerCase();
      const storedLang = localStorage.getItem("language");

      // 优先使用 localStorage 存储的语言
      if (storedLang) return storedLang;

      // 根据浏览器语言返回
      if (browserLang.startsWith("zh")) return "zh";
      if (browserLang.startsWith("en")) return "en";

      // 默认中文
      return "zh";
    },
    currentLanguageName() {
      return this.languageMap[this.$i18n.locale];
    },
    orderedLanguageMap() {
      const browserLang = (
        navigator.language || navigator.userLanguage
      ).toLowerCase();
      const isEnglishBrowser = browserLang.startsWith("en");

      return isEnglishBrowser
        ? { en: "English", zh: "中文" }
        : { zh: "中文", en: "English" };
    },
    trips() {
      return this.$t("trips.list");
    },
  },
  created() {
    // 设置初始语言
    this.$i18n.locale = this.currentLanguage;
    localStorage.setItem("language", this.currentLanguage);
  },
  methods: {
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    changeLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem("language", lang);
      this.showLanguageDropdown = false;
    },
    gochatbox(query) {
      location.href = "https://app.tripseek.cn/chat/6WDpv4lTX8lLkdXr?query=" + query;
    }
  },
};
</script>

<style lang="less" scoped>
.home {
  min-height: 100vh;
  padding: 20px 0 0 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  max-width: 100%;
  margin: 0 auto;

  @media (min-width: 1536px) {
    max-width: 1536px;
  }
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  //background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(50px);
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  margin: 0 auto;
  .nav {
    max-width: 100%;
    margin: 0 auto;
    padding: 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (min-width: 1536px) {
      max-width: 1536px;
    }
    .logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.25rem;
      font-weight: 600;
      color: #0051fe;

      img {
        height: 24px;
      }
    }

    .language-selector {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 4px;
      transition: background-color 0.2s;
      position: relative;

      &:hover {
        //background-color: rgba(0, 0, 0, 0.05);
      }

      .language-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        min-width: 120px;

        .language-option {
          padding: 0.5rem 1rem;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover {
            background-color: #f3f4f6;
          }

          &.active {
            // background-color: #f3f4f6;
            font-weight: bold;
            color: #1a56db;
          }
        }
      }
    }
  }
}

.hero {
  padding: 8rem 2rem 4rem;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  margin: 0 auto;

  &-content {
    max-width: 600px;
  }

  &-title {
    font-size: 3.5rem;
    line-height: 1.2;
    margin-bottom: 1.5rem;
    color: #1f2937;
    .slogan{display: block;}
    .highlight {
      color: #1a56db;
    }
  }

  &-description {
    font-size: 1.25rem;
    color: #4b5563;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  &-image {
    width: 50%;
    height: 600px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem 0;

    @media (max-width: 768px) {
      width: 100%;
      height: auto;
      margin-top: 2rem;
      padding: 1rem 0;
    }
  }
}

.new-feature {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  background: #f0f7ff;
  padding: 0.75rem 0;
  border-radius: 999px;

  .badge {
    background: #1a56db;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 500;
    margin-right: 10px;
    text-align: center;
  }

  .feature-text {
    color: #1f2937;
    font-size: 0.875rem;
  }
}

.trips {
  padding: 4rem 2rem;
  max-width: 100%;
  margin: 0 auto;

  h2 {
    text-align: center;
    font-size: 2rem;
    color: #1f2937;
    margin-bottom: 3rem;
  }

  &-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }
}

.trip-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
  }

  .trip-content {
    padding: 1.5rem;

    h3 {
      font-size: 1.125rem;
      color: #1f2937;
      margin-bottom: 1rem;
    }
  }
}

.ai-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(26, 86, 219, 0.1);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.875rem;
  color: #1a56db;

  i {
    font-size: 1rem;
    color: #1a56db;
    margin-right: 10px;
  }
}

.ai-assistant {
  // background: #f8fafc;
  padding: 3rem 1rem;

  .assistant-content {
    max-width: 100%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4rem;
    background: #ffffff;
    border-radius: 16px;
    padding: 3rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

    .text-content {
      flex: 1;

      h2 {
        font-size: 2rem;
        color: #1f2937;
        margin-bottom: 1.5rem;
      }

      p {
        color: #4b5563;
        margin-bottom: 2rem;
        line-height: 1.6;
      }

      .features-list {
        list-style: none;
        padding: 0;

        li {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          color: #1f2937;

          i {
            color: #1a56db;
          }
        }
      }
    }

    .assistant-image {
      width: 200px;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        color: #1a56db;
      }
    }
  }
}

.footer {
  width: 100%;
  // background: #f3f4f6;
  color: #1f2937;
  text-align: center;
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  // border-top: 1px solid rgba(26, 86, 219, 0.1);

  p {
    max-width: 800px;
    text-align: center;
    font-weight: 400;
    // line-height: 1.6;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 6rem 1rem 1rem;
    flex-direction: column;
    text-align: center;

    &-title {
      font-size: 2.5rem;
    }

    &-description {
      font-size: 1.125rem;
    }
  }

  .new-feature {
    flex-direction: column;
    gap: 0.5rem;
  }

  .trips {
    padding: 3rem 1rem;

    &-grid {
      grid-template-columns: 1fr;
    }
  }

  .ai-assistant {
    padding: 1rem 1rem;
    .assistant-content {
      flex-direction: column;
      text-align: center;
      padding: 2rem;

      .features-list li {
        justify-content: center;
        i {
          margin-right: 10px;
        }
      }

      .assistant-image {
        width: 150px;
        height: 150px;
        margin: 0 auto;
      }
    }
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .nav {
    padding: 1rem;
  }

  .trips h2 {
    font-size: 1.5rem;
  }
}
</style>
